import React, { useEffect, useRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => (d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "");

// Parse an ISO 'yyyy-mm-dd' string into a local Date (no timezone shift).
function parseISO(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v || "");
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return isNaN(d.getTime()) ? null : d;
}

function isoToDisplay(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

function maskDigits(digits) {
  const d = digits.slice(0, 8);
  let out = d.slice(0, 2);
  if (d.length > 2) out += "/" + d.slice(2, 4);
  if (d.length > 4) out += "/" + d.slice(4, 8);
  return out;
}

// A masked date field (DD/MM/YYYY) that lets you type straight through
// day → month → year without stopping, plus a calendar popover. Emits an
// ISO 'yyyy-mm-dd' string via onChange (or "" when empty/incomplete).
export function DateField({ value, onChange, className = "", testid, disabled }) {
  const [text, setText] = useState(() => isoToDisplay(value));
  const editing = useRef(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!editing.current) setText(isoToDisplay(value));
  }, [value]);

  const commit = (digits) => {
    if (digits.length === 8) {
      const dd = +digits.slice(0, 2), mm = +digits.slice(2, 4), yy = +digits.slice(4, 8);
      const d = new Date(yy, mm - 1, dd);
      if (!isNaN(d.getTime()) && d.getDate() === dd && d.getMonth() === mm - 1) {
        onChange(toISO(d));
        return;
      }
    }
    if (digits.length === 0) onChange("");
  };

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    editing.current = true;
    setText(maskDigits(digits));
    commit(digits);
  };

  const dateObj = parseISO(value);
  const year = new Date().getFullYear();

  return (
    <div className={`relative ${className}`}>
      <Input
        value={text}
        onChange={handleChange}
        onBlur={() => { editing.current = false; setText(isoToDisplay(value)); }}
        placeholder="DD/MM/YYYY"
        inputMode="numeric"
        disabled={disabled}
        data-testid={testid}
        className="pr-9"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testid ? `${testid}-calendar` : undefined}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
            tabIndex={-1}
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-popover" align="end">
          <Calendar
            mode="single"
            selected={dateObj || undefined}
            defaultMonth={dateObj || undefined}
            captionLayout="dropdown-buttons"
            fromYear={1940}
            toYear={year + 10}
            classNames={{ caption_label: "hidden", caption_dropdowns: "flex gap-2 items-center", dropdown: "border border-border rounded-md text-sm px-1 py-0.5 bg-background" }}
            onSelect={(d) => { editing.current = false; onChange(toISO(d)); setText(d ? isoToDisplay(toISO(d)) : ""); setOpen(false); }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default DateField;
